import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../api";

export function isPreviewable(file) {
  const type = file.file_type || "";
  const ext = (file.original_filename || "").split(".").pop().toLowerCase();
  const isImage = type.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
  const isPdf = type === "application/pdf" || ext === "pdf";
  const isText = type.startsWith("text/") || ext === "txt";
  return isImage || isPdf || isText;
}

const DocumentPreview = ({ file, onClose }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [textContent, setTextContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const type = file.file_type || "";
  const ext = (file.original_filename || "").split(".").pop().toLowerCase();
  const isImage = type.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
  const isPdf = type === "application/pdf" || ext === "pdf";
  const isText = type.startsWith("text/") || ext === "txt";
  const canPreview = isImage || isPdf || isText;

  useEffect(() => {
    let url;
    const load = async () => {
      if (!canPreview) {
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/files/${file.id}`, { responseType: "blob" });
        if (isText) {
          setTextContent(await res.data.text());
        } else {
          url = URL.createObjectURL(res.data);
          setBlobUrl(url);
        }
      } catch (e) {
        console.error("Preview failed:", e);
        setError("Failed to load preview.");
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line
  }, [file.id]);

  return (
    <div
      className="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center p-4 z-50"
      data-testid="document-preview-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900 truncate pr-4" data-testid="preview-filename">
            {file.original_filename}
          </h3>
          <button
            onClick={onClose}
            data-testid="preview-close-button"
            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4 overflow-auto flex-1 flex items-center justify-center bg-gray-50" style={{ minHeight: 240 }}>
          {loading ? (
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" data-testid="preview-loading"></div>
          ) : error ? (
            <p className="text-red-600 text-sm" data-testid="preview-error">{error}</p>
          ) : !canPreview ? (
            <div className="text-center text-gray-600 text-sm" data-testid="preview-unavailable">
              Preview isn't available for this file type. Use Download to view it.
            </div>
          ) : isImage ? (
            <img
              src={blobUrl}
              alt={file.original_filename}
              className="max-w-full max-h-[70vh] object-contain"
              data-testid="preview-image"
            />
          ) : isPdf ? (
            <iframe title="document preview" src={blobUrl} className="w-full h-[70vh] border-0" data-testid="preview-pdf" />
          ) : (
            <pre
              className="w-full text-xs text-gray-800 whitespace-pre-wrap break-words font-mono"
              data-testid="preview-text"
            >
              {textContent}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentPreview;
